/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * Item component for ExtFilterConstructor component
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.0.0
 */

import { ChangeEvent } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '@assets/index';
import { DropDownFilterButtonSize } from '@components/common/DropDownFilter/DropDownFilter';
import { BlockWithIcon, Input, Select } from '@components/common/index';
import { EInputKind } from '@components/common/Input/Input';
import { IOptionItemProps } from '@components/common/Select/Select';
import { KeyboardKeyCodeEnum } from '@modules/app/appConstants';
import { IRootReducer } from '@reducers/rootReducer';

import { setExtFilterTabIndex } from '../../actions/extFilterApiActions';
import { makeSelectExtFilterTabIndex } from '../../extFilterSelectors';
import {
    ExtFilterAttrTypeEnum,
    ExtFilterOnChangeTypes,
    IExtFilterItem,
    IExtFilterItemValue,
} from '../../types/extFilterTypes';
import {
    AddContainerStyled,
    DeleteContainerStyled,
    ExtFilterConstructorItemStyled,
    ExtFilterItemContainerStyled,
    ExtFilterItemStubContainerStyled,
} from '../styled/ExtFilterConstructorBlockStyled';
import { getSelected } from './ExtFilterConstructorBlockUitls';

interface IConnectedProps {
    tabIndex?: number;
}

interface IDispatchProps {
    setExtFilterTabIndex: (payload: number) => void;
}

/**
 * @description options list for ExtFilterItem
 * @property {string} attrPlaceholder attr name placholder
 * @property {ExtFilterAttrTypeEnum} attrType attr value type
 * @property {IOptionItemProps[]} attrOptions options for attr name
 * @property {IOptionItemProps[]} operationOptions options for operations
 * @property {IOptionItemProps[]} operatorsOptions options for operators
 * @property {IOptionItemProps[]} valueOptions options for value select
 */
export interface IFilterConstructorItemOptions {
    attrOptions: IOptionItemProps[];
    attrPlaceholder: string;
    attrType?: ExtFilterAttrTypeEnum;
    operationOptions?: IOptionItemProps[];
    operatorsOptions?: IOptionItemProps[];
    valueOptions?: IOptionItemProps[];
    valuePlaceholder?: string;
}

/**
 * @description options list for ExtFilterItem
 * @property {number} index filter item index
 * @property {IExtFilterItem} item filter item
 * @property {boolean} lastIndex last element flag
 * @property {Function} onChange onChagne callback
 * @property {IFilterConstructorItemOptions} options options for FilterItem select
 * @property {boolean} highlightEmpty highlight empty fields flag
 */
export interface IExtFilterConstructorItemProps {
    highlightEmpty: boolean;
    index: number;
    item: IExtFilterItem;
    lastIndex: boolean;
    onChange: (arg: IExtFilterItemValue) => void;
    options: IFilterConstructorItemOptions;
}

type ExtFilterConstructor = IExtFilterConstructorItemProps & IDispatchProps & IConnectedProps;

export const ExtFilterConstructorItem = (props: ExtFilterConstructor) => {
    const selectedOperation = getSelected(props.item.operation, props.options.operationOptions);
    const selectedAttr = getSelected(props.item.attr, props.options.attrOptions);
    const selectedOperators = getSelected(props.item.operator, props.options.operatorsOptions);
    const selectedValue = props.options.attrType === ExtFilterAttrTypeEnum.TEXT
        ? props.item.value
        : getSelected(props.item.value, props.options.valueOptions);

    const onChangeHandler = (type: string, eventValue?: ExtFilterOnChangeTypes) => {
        props.onChange({
            type,
            index: props.index,
            value: eventValue,
        });
    };
    const onSelectOperationHandler = (event: IOptionItemProps) => {
        onChangeHandler('operation', event);
    };
    const onSelectAttrHandler = (event: IOptionItemProps) => {
        onChangeHandler('attr', event);
    };
    const onSelectOperatorHandler = (event: IOptionItemProps) => {
        onChangeHandler('operators', event);
    };
    const onSelectValueTextHandler = (event: ChangeEvent<HTMLInputElement>) => {
        onChangeHandler('valueText', event);
    };
    const onSelectValueSelectHandler = (event: IOptionItemProps) => {
        onChangeHandler('valueSelect', event);
    };
    const onKeyDownHandler = (event: React.KeyboardEvent, type: string) => {
        if (event.keyCode === KeyboardKeyCodeEnum.ENTER) {
            if (type === 'add') {
                onChangeHandler('add');
                props.setExtFilterTabIndex(props.index + 1);
            } else if (type === 'delete') {
                onChangeHandler('delete');
            }
        }
    };
    const onAddContainer = () => {
        onChangeHandler('add');
        props.setExtFilterTabIndex(props.index + 1);
    };

    return (
        <ExtFilterConstructorItemStyled tabIndex={props.index}>
            {
                props.options.operationOptions ?
                    <ExtFilterItemContainerStyled
                        className="ext-filter__constructor-item constructor-item__operation"
                        data-extended-filter-constructor-item="extendedFilterConstructorItemOperation"
                        width={105}
                    >
                        <Select
                            classNamePrefix="ext-filter-"
                            filterButtonSize={DropDownFilterButtonSize.medium}
                            options={props.options.operationOptions}
                            selectedOption={selectedOperation}
                            onSelect={onSelectOperationHandler}
                        />
                    </ExtFilterItemContainerStyled>
                    :
                    <ExtFilterItemStubContainerStyled width={105} />
            }
            <ExtFilterItemContainerStyled
                className="ext-filter__constructor-item constructor-item__attr"
                data-extended-filter-constructor-item="extendedFilterConstructorItemAttr"
                width={304}
            >
                <Select
                    classNamePrefix="ext-filter-"
                    filterButtonSize={DropDownFilterButtonSize.medium}
                    kind={props.highlightEmpty && !selectedAttr ? 'error' : 'normal'}
                    options={props.options.attrOptions}
                    placeholder={props.options.attrPlaceholder}
                    selectedOption={selectedAttr}
                    onSelect={onSelectAttrHandler}
                />
            </ExtFilterItemContainerStyled>
            {
                props.options.operatorsOptions ?
                    <ExtFilterItemContainerStyled
                        className="ext-filter__constructor-item constructor-item__operators"
                        data-extended-filter-constructor-item="extendedFilterConstructorItemOperators"
                        width={168}
                    >
                        <Select
                            classNamePrefix="ext-filter-"
                            filterButtonSize={DropDownFilterButtonSize.medium}
                            options={props.options.operatorsOptions}
                            selectedOption={selectedOperators}
                            onSelect={onSelectOperatorHandler}
                        />
                    </ExtFilterItemContainerStyled>
                    :
                    <ExtFilterItemStubContainerStyled showBackground={true} width={168} />
            }
            {
                props.options.operatorsOptions ?
                    <ExtFilterItemContainerStyled
                        className="ext-filter__constructor-item constructor-item__value"
                        data-extended-filter-constructor-item="extendedFilterConstructorItemValue"
                        width={237}
                    >
                        {
                            props.options.attrType === ExtFilterAttrTypeEnum.TEXT ?
                                <Input
                                    className={[
                                        'constructor-item__value',
                                        'value__input',
                                        props.item.value ? 'filled' : '',
                                    ].join(' ')}
                                    kind={
                                        props.highlightEmpty && !props.item.value
                                            ? EInputKind.error
                                            : EInputKind.normal
                                    }
                                    value={props.item.value}
                                    onChange={onSelectValueTextHandler}
                                />
                                :
                                <Select
                                    classNamePrefix="ext-filter-"
                                    filterButtonSize={DropDownFilterButtonSize.medium}
                                    options={props.options.valueOptions}
                                    selectedOption={selectedValue as IOptionItemProps}
                                    onSelect={onSelectValueSelectHandler}
                                />
                        }
                    </ExtFilterItemContainerStyled>
                    :
                    <ExtFilterItemStubContainerStyled showBackground={true} width={237} />
            }
            {
                !!props.index &&
                <DeleteContainerStyled
                    onClick={() => onChangeHandler('delete')}
                    onKeyDown={(e: React.KeyboardEvent) => onKeyDownHandler(e, 'delete')}
                >
                    <BlockWithIcon
                        className="page-title__block-with-icon block-with-icon__update"
                        iconLeft={icons.cancelIcon}
                        iconLeftSize={40}
                    />
                </DeleteContainerStyled>
            }
            {
                props.lastIndex &&
                <AddContainerStyled
                    onClick={onAddContainer}
                    onKeyDown={(e: React.KeyboardEvent) => onKeyDownHandler(e, 'add')}
                >
                    <BlockWithIcon
                        className="page-title__block-with-icon block-with-icon__update"
                        iconLeft={icons.addIcon}
                        iconLeftSize={24}
                    />
                </AddContainerStyled>
            }
            {
                !props.index &&
                <DeleteContainerStyled />
            }
        </ExtFilterConstructorItemStyled>
    );
};

const mapStateToProps = createStructuredSelector<IRootReducer, IExtFilterConstructorItemProps, IConnectedProps>({
    tabIndex: makeSelectExtFilterTabIndex(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    setExtFilterTabIndex: (payload: number) => dispatch(setExtFilterTabIndex(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ExtFilterConstructorItem);
